import Promise from 'bluebird';
import { AjaxEventHandler } from '@bingads-webui/instrumentation-stubs';

/**
 * Class to intercept 302 response from CC, and perform a custom redirect.
 *
 * jQuery ajax doesn't work well with HTTP status code 302,
 * (see https://github.com/jquery/jquery/issues/3467), which CC uses to redirect
 * us to a default sign-in page.
 *
 * This 302 doesn't work for MCA, as we'll be serving from ui.*.bingads.microsoft.com,
 * whereas the redirect URL from 302 doesn't contain the "ui." portion, causing
 * a CORS violation exception.
 *
 * We, however, could leverage this exception, and do a manual redirect
 * to our intended sign-out landing page, instead of the URL from the 302 response.
 */
export class AjaxEventHandlerWithSignOutRedirect extends AjaxEventHandler {
  constructor(options = {}) {
    super(options);
    this.serverContext = options.serverContext || {};
    this.logger = options.logger;
    this.isUnloading = false;

    // detect if browser is currently unloading the page
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Sets a flag if the user is trying to refresh the page.
   * Errors will be generated if CC API requests are cancelled due to page refresh.
   * @returns {Promise} - Promise that runs if page does not get unloaded after a short period.
   */
  handleBeforeUnload = () => {
    this.isUnloading = true;

    // if unload gets cancelled, set isUnloading to false
    return Promise.delay(200).then(() => { this.isUnloading = false; });
  }

  errorHandler(ajaxContext, options) {
    const [jqXHR, textStatus, errorThrown] = options;
    AjaxEventHandler.prototype.errorHandler.call(
      this,
      ajaxContext, [jqXHR, textStatus, errorThrown]
    );

    // jqXHR.status will always be 0 in this case, so we can't rely on it
    // status 0 means request was cancelled due to CORS violation or ad blocker or page reload

    // TODO: This is a hacky solution, CC should give us a JSON response with authentication error
    // rather than giving a 3xx redirect for an API request.
    if (!this.isUnloading && textStatus === 'error' && ajaxContext.url.includes('?rurl=')) {
      const { Urls: { SignOutUrl = null } = {} } = this.serverContext;

      if (this.logger) {
        this.logger.trace(`Sign out via AjaxEventHandlerWithSignOutRedirect: ${SignOutUrl}`);
      }

      if (SignOutUrl) {
        window.location.href = SignOutUrl;
      }
    }
  }
}
